/**
 * The timesheet contract — policy, time entries, timesheet periods, submit/decide/withdraw,
 * lock, utilization, and the Daily Work Plan.
 *
 * Copies leave.contract.ts's conventions exactly (see that file's header for the full rationale):
 * DTOs distinct from the Prisma models, `paginatedSchema()` for every list.
 *
 * MINUTES, NOT HOURS. `TimeEntry.minutes` is an `Int`; two decimal places of an hour (`0.42`) does
 * not round-trip an arbitrary minute value (25 min -> 0.4166...h -> stored 0.42 -> reads back as
 * 25.2 min). Hours are a display/comparison unit only, converted once in `@hrms/capacity` — never
 * `Number(minutes) / 60` at a call site or in this contract.
 *
 * NO DENORMALISED TOTALS ON `Timesheet`. `totalMinutes`/`billableMinutes` on `timesheetSchema` are
 * computed at READ time from `timeEntry.groupBy({by: ['timesheetId'], _sum: {minutes: true}})`,
 * never a stored column — a hand-maintained counter drifts the first time a row is edited outside
 * the one path that would otherwise maintain it.
 *
 * APPROVAL GOES THROUGH `@hrms/workflow`, NEVER A HAND-ROLLED STATUS FLIP (CLAUDE.md invariant 6).
 * `timesheetSchema.status` mirrors whatever the underlying `WorkflowInstance` says — the route
 * calls `submitApprovalRequest`/`decide`/`withdraw` (via `apps/api/src/lib/approval-requests.ts`)
 * and copies the resulting status across, exactly like `leave.contract.ts`'s routes do.
 *
 * `decideTimesheet` takes one combined `{action: 'approve' | 'reject'}` body rather than two
 * routes, matching `decideRegularisationPayloadSchema`'s shape in `attendance.contract.ts` — the
 * plan names this one route `decideTimesheet`, not a pair.
 */

import { initContract } from '@ts-rest/core';
import { TIMESHEET_STATUSES } from '../enums.js';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import {
  attendanceCheckModeSchema,
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  legalEntityIdSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  timeEntrySourceSchema,
  timesheetPeriodTypeSchema,
  timesheetStatusSchema,
  uuidSchema,
} from '../schemas/common.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

// ---- Policy -----------------------------------------------------------------

/**
 * Per-org, optionally per-legal-entity, timesheet rules. `legalEntityId: null` is the org-default
 * row. A TABLE, not a JSON settings blob, because the reminder/auto-lock workers query "which
 * tenants have reminders due today" on a schedule — see schema.prisma's model comment.
 */
export const timesheetPolicySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema.nullable(),
  periodType: timesheetPeriodTypeSchema,
  weekStartDay: z.number().int().min(0).max(6),
  requireSubmission: z.boolean(),
  submissionDueDays: z.number().int().nonnegative(),
  autoLockDays: z.number().int().nonnegative(),
  requireProjectOnEntry: z.boolean(),
  maxMinutesPerDay: z.number().int().positive(),
  allowFutureEntries: z.boolean(),
  /** Master switch (plan D6): false = timesheets are off for this legal entity / org default. */
  enabled: z.boolean(),
  /** Logged project time vs attendance (plan 6.6): off | warn | block. */
  attendanceCheckMode: attendanceCheckModeSchema,
  /** Tolerance, in minutes, before a day counts as mismatched under `attendanceCheckMode`. */
  maxVarianceMinutes: z.number().int().nonnegative(),
  /**
   * Opt-in, independent of `attendanceCheckMode`. OFF (default): a day with zero/no attendance and
   * logged hours is never flagged — unchanged. ON: such a day is flagged `unverified_hours` — a
   * warning only, never a block, regardless of `attendanceCheckMode`. See
   * PM_FINAL_SCOPE_SPECIFICATION.md Part 5.
   */
  flagUnverifiedHours: z.boolean(),
  /**
   * Days after a task's due date that time may still be logged against it; after that the task is closed for time.
   * `null` (the default) = unlimited, i.e. the behaviour before this setting existed. Applies to time entries and to
   * the hours a check-out report prefills.
   */
  taskWorkGraceDays: z.number().int().min(0).max(365).nullable(),
});
export type TimesheetPolicy = z.infer<typeof timesheetPolicySchema>;

// ---- Time entries -------------------------------------------------------------

/**
 * One line of work. `projectName` is a NAME SNAPSHOT taken when `projectId` is set, so archiving
 * or renaming a project never rewrites history — see schema.prisma's `TimeEntry` model comment.
 */
/**
 * Logged time vs attendance for one day (plan 6.6). Computed at read time, never stored — attendance can be regularised
 * after an entry was made. `flagged` = the days `warn` asks to confirm and `block` refuses.
 */
export const dayCheckStatusSchema = z.enum([
  'ok',
  'over',
  'no_attendance',
  'open_punch',
  'non_working_day',
  /** `flagUnverifiedHours` ON + zero/no attendance + logged hours. A warning only, never a block —
   * see PM_FINAL_SCOPE_SPECIFICATION.md Part 5. Not yet produced anywhere; `evaluateDay` gains this
   * branch in the flagUnverifiedHours implementation PR. */
  'unverified_hours',
]);
export const dayCheckSchema = z.object({
  date: dateOnlySchema,
  loggedMinutes: z.number().int().nonnegative(),
  /** Minutes worked per attendance; null when there is nothing to compare against. */
  attendanceMinutes: z.number().int().nonnegative().nullable(),
  status: dayCheckStatusSchema,
  flagged: z.boolean(),
});
export type DayCheck = z.infer<typeof dayCheckSchema>;

export const timeEntrySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  attendanceDayId: uuidSchema.nullable(),
  timesheetId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  projectName: z.string(),
  taskId: uuidSchema.nullable(),
  taskTitle: z.string().nullable(),
  date: dateOnlySchema,
  section: z.string().nullable(),
  activity: z.string().nullable(),
  minutes: z.number().int().nonnegative(),
  isBillable: z.boolean(),
  source: timeEntrySourceSchema,
  remarks: z.string().nullable(),
  /** Set only on a create/update response, when `attendanceCheckMode = warn` and this day now exceeds attendance. */
  attendanceWarning: z.string().nullable(),
  /** PM-015 (Keka gap closure, follow-up round 3) — live timer. Both null except for a `source:
   * 'timer'` row; `startedAt` set + `endedAt` null = currently running. */
  startedAt: instantSchema.nullable(),
  endedAt: instantSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type TimeEntry = z.infer<typeof timeEntrySchema>;

// ---- Timesheets ---------------------------------------------------------------

/**
 * The period envelope: one person, one period, one approval. Line items are `TimeEntry` rows
 * pointing back via `timesheetId` — same relationship `AttendanceDay` has to `AttendancePunch`.
 * `totalMinutes`/`billableMinutes` are computed at read time — see this file's header comment.
 */
export const timesheetSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  periodType: timesheetPeriodTypeSchema,
  status: timesheetStatusSchema,
  submittedAt: instantSchema.nullable(),
  decidedAt: instantSchema.nullable(),
  workflowInstanceId: uuidSchema.nullable(),
  remarks: z.string().nullable(),
  totalMinutes: z.number().int().nonnegative(),
  billableMinutes: z.number().int().nonnegative(),
  /** One row per day with logged time: logged vs attendance. Empty when `attendanceCheckMode = off`, and on list responses. */
  dayChecks: z.array(dayCheckSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type Timesheet = z.infer<typeof timesheetSchema>;

// ---- Register -----------------------------------------------------------------

/** A timesheet's status, or `not_started` for someone with no timesheet for the period yet. */
export const registerStatusSchema = z.enum(['not_started', ...TIMESHEET_STATUSES]);

/** One person's timesheet for one period, whether or not they have started it — the "who has not submitted" view. */
export const timesheetRegisterRowSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  legalEntityId: uuidSchema,
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  timesheetId: uuidSchema.nullable(),
  status: registerStatusSchema,
  /** Period end plus the policy's `submissionDueDays`. */
  dueDate: dateOnlySchema,
  overdue: z.boolean(),
  totalMinutes: z.number().int().nonnegative(),
  /** Days whose logged time exceeds attendance (0 when the check is off). */
  flaggedDays: z.number().int().nonnegative(),
});
export type TimesheetRegisterRow = z.infer<typeof timesheetRegisterRowSchema>;

export const registerResponseSchema = z.object({
  items: z.array(timesheetRegisterRowSchema),
  /** True when the scope holds more people than one response returns — narrow the filters. */
  truncated: z.boolean(),
});
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

// ---- Utilization --------------------------------------------------------------

export const utilizationRowSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  capacityMinutes: z.number().int().nonnegative(),
  loggedMinutes: z.number().int().nonnegative(),
  billableMinutes: z.number().int().nonnegative(),
  utilizationPct: z.number(),
  billableUtilizationPct: z.number(),
  realizationPct: z.number(),
});
export type UtilizationRow = z.infer<typeof utilizationRowSchema>;

export const utilizationResponseSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  rows: z.array(utilizationRowSchema),
});
export type UtilizationResponse = z.infer<typeof utilizationResponseSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

// ---- Policy -----------------------------------------------------------------

export const getPolicyQuerySchema = z.object({
  /** Omitted = the org-default row (legalEntityId IS NULL). */
  legalEntityId: legalEntityIdSchema.optional(),
});

export const updatePolicyBodySchema = z.object({
  /** Omitted = update the org-default row. */
  legalEntityId: legalEntityIdSchema.optional(),
  periodType: timesheetPeriodTypeSchema.optional(),
  weekStartDay: z.number().int().min(0).max(6).optional(),
  requireSubmission: z.boolean().optional(),
  submissionDueDays: z.number().int().nonnegative().optional(),
  autoLockDays: z.number().int().nonnegative().optional(),
  requireProjectOnEntry: z.boolean().optional(),
  maxMinutesPerDay: z.number().int().positive().optional(),
  allowFutureEntries: z.boolean().optional(),
  enabled: z.boolean().optional(),
  attendanceCheckMode: attendanceCheckModeSchema.optional(),
  maxVarianceMinutes: z.number().int().nonnegative().optional(),
  flagUnverifiedHours: z.boolean().optional(),
  taskWorkGraceDays: z.number().int().min(0).max(365).nullable().optional(),
});

// ---- Timesheets ---------------------------------------------------------------

export const listTimesheetsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller's own timesheets. */
  personId: uuidSchema.optional(),
  status: timesheetStatusSchema.optional(),
  periodStart: dateOnlySchema.optional(),
  periodEnd: dateOnlySchema.optional(),
});

export const getMyTimesheetQuerySchema = z.object({
  /** Omitted = the current open period, per the caller's effective TimesheetPolicy. */
  periodStart: dateOnlySchema.optional(),
});

// ---- Time entries -------------------------------------------------------------

export const listEntriesQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller's own entries. */
  personId: uuidSchema.optional(),
  timesheetId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  source: timeEntrySourceSchema.optional(),
});

export const createEntryBodySchema = z.object({
  date: dateOnlySchema,
  projectId: uuidSchema.nullable().optional(),
  taskId: uuidSchema.nullable().optional(),
  section: z.string().trim().max(200).optional(),
  activity: z.string().trim().max(200).optional(),
  minutes: z.number().int().positive().max(1440),
  isBillable: z.boolean().optional(),
  source: timeEntrySourceSchema.default('timesheet'),
  remarks: z.string().trim().max(1000).optional(),
});

// ---- Live timer (PM-015) -------------------------------------------------------
//
// A `TimeEntry` with `source: 'timer'` and `endedAt: null` IS the running timer — no separate
// table. At most one running timer per person at a time: `startTimer` 409s rather than
// auto-stopping the old one (safer and more honest than silently discarding unsaved time).

export const startTimerBodySchema = z.object({
  projectId: uuidSchema.optional(),
  taskId: uuidSchema.optional(),
  isBillable: z.boolean().optional(),
  remarks: z.string().trim().max(1000).optional(),
});

// ---- Time entries (continued) --------------------------------------------------

/** Only reachable while the owning timesheet is `draft` or `rejected` — the route enforces that, not this schema. */
export const updateEntryBodySchema = z.object({
  date: dateOnlySchema.optional(),
  projectId: uuidSchema.nullable().optional(),
  taskId: uuidSchema.nullable().optional(),
  section: z.string().trim().max(200).nullable().optional(),
  activity: z.string().trim().max(200).nullable().optional(),
  minutes: z.number().int().positive().max(1440).optional(),
  isBillable: z.boolean().optional(),
  remarks: z.string().trim().max(1000).nullable().optional(),
});

// ---- Decide / submit / withdraw / lock -----------------------------------------

export const submitTimesheetBodySchema = z.object({
  remarks: z.string().trim().max(500).optional(),
  /** Under `attendanceCheckMode = warn`: the employee has seen the days that exceed attendance and submits anyway. */
  confirmFlaggedDays: z.boolean().optional(),
});

export const getRegisterQuerySchema = z.object({
  /** Any date inside the period to show; each person's own policy decides where that period starts and ends. Default: today. */
  on: dateOnlySchema.optional(),
  legalEntityId: legalEntityIdSchema.optional(),
  status: registerStatusSchema.optional(),
  overdueOnly: z.enum(['true', 'false']).optional(),
});

/** Combined action, matching decideRegularisationPayloadSchema — see this file's header comment. */
export const decideTimesheetBodySchema = z.object({
  action: z.enum(['approve', 'reject']),
  remarks: z.string().trim().max(500).optional(),
});

export const lockTimesheetsBodySchema = z
  .object({
    /** Lock every eligible timesheet whose period ended on or before this date. */
    periodEnd: dateOnlySchema.optional(),
    /** Or lock these specific timesheets by id. */
    timesheetIds: z.array(uuidSchema).optional(),
  })
  .refine((body) => body.periodEnd != null || (body.timesheetIds?.length ?? 0) > 0, {
    message: 'Provide either periodEnd or a non-empty timesheetIds',
    path: ['periodEnd'],
  });

export const lockTimesheetsResponseSchema = z.object({
  lockedCount: z.number().int().nonnegative(),
});

// ---- Utilization --------------------------------------------------------------

export const getUtilizationQuerySchema = z.object({
  /** Defaults to the caller's own visible scope (self, or reporting tree for a manager). */
  personId: uuidSchema.optional(),
  from: dateOnlySchema,
  to: dateOnlySchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const timesheetContract = c.router(
  {
    // ---- Policy -----------------------------------------------------------
    getPolicy: {
      method: 'GET',
      path: '/policy',
      query: getPolicyQuerySchema,
      responses: { 200: timesheetPolicySchema, 401: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta(null),
      summary: 'Get the effective timesheet policy (entity row, falling back to org default)',
    },
    updatePolicy: {
      method: 'PATCH',
      path: '/policy',
      body: updatePolicyBodySchema,
      responses: {
        200: timesheetPolicySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta('org.settings:update'),
      summary: 'Create or update the org-default or a per-legal-entity timesheet policy',
    },

    // ---- Timesheets ---------------------------------------------------------
    getMyTimesheet: {
      method: 'GET',
      path: '/me',
      query: getMyTimesheetQuerySchema,
      responses: { 200: timesheetSchema.nullable(), 401: errorBodySchema },
      metadata: routeMeta(null),
      summary: "The caller's timesheet for a period; null if the worker hasn't opened it yet",
    },
    listTimesheets: {
      method: 'GET',
      path: '/',
      query: listTimesheetsQuerySchema,
      responses: {
        200: paginatedSchema(timesheetSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: "List timesheets in the caller's visible scope",
    },
    // ---- Time entries -------------------------------------------------------
    listEntries: {
      method: 'GET',
      path: '/entries',
      query: listEntriesQuerySchema,
      responses: { 200: paginatedSchema(timeEntrySchema), 401: errorBodySchema },
      metadata: routeMeta(null),
      summary: "List time entries in the caller's visible scope",
    },
    createEntry: {
      method: 'POST',
      path: '/entries',
      body: createEntryBodySchema,
      responses: {
        201: timeEntrySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        412: errorBodySchema,
        422: errorBodySchema,
      },
      metadata: routeMeta('timesheet:submit'),
      summary: 'Log a time entry against a project/task',
    },
    updateEntry: {
      method: 'PATCH',
      path: '/entries/:entryId',
      body: updateEntryBodySchema,
      responses: {
        200: timeEntrySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        // The owning timesheet is submitted, approved or locked — edits are draft-or-rejected only.
        412: errorBodySchema,
      },
      metadata: routeMeta(['timesheet:submit', 'timesheet:read']),
      summary: 'Update a time entry — only while its timesheet is draft or rejected',
    },
    deleteEntry: {
      method: 'DELETE',
      path: '/entries/:entryId',
      // Matches attendance.contract.ts's DELETE routes (e.g. roster entries) rather than 204 —
      // this codebase's DELETE convention is a 200 confirmation body, not an empty response.
      body: c.type<undefined>(),
      responses: {
        200: z.object({ success: z.boolean(), message: z.string() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(['timesheet:submit', 'timesheet:read']),
      summary: 'Delete a time entry — only while its timesheet is draft or rejected',
    },

    // ---- Live timer (PM-015) -------------------------------------------------
    startTimer: {
      method: 'POST',
      path: '/entries/timer/start',
      body: startTimerBodySchema,
      responses: {
        201: timeEntrySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        // Timesheets off for this legal entity/org, or a policy check (requireProjectOnEntry, an
        // unknown project/task) — same shape createEntry already uses.
        412: errorBodySchema,
        422: errorBodySchema,
        /** A timer is already running for this person. */
        409: errorBodySchema,
      },
      // AI-visible (default): small typed JSON body/response, same shape as createEntry's own
      // proposal-only write — a model can satisfy "start a timer on task X" without hallucinating
      // any field.
      metadata: routeMeta('timesheet:submit'),
      summary: 'Start a live timer against a project/task',
    },
    stopTimer: {
      method: 'POST',
      path: '/entries/timer/:entryId/stop',
      body: c.type<undefined>(),
      responses: {
        200: timeEntrySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not a running timer (already stopped, or not a timer entry at all). */
        409: errorBodySchema,
      },
      // AI-visible (default): no request body to hallucinate, and the response is the caller's own
      // time entry — nothing sensitive.
      metadata: routeMeta('timesheet:submit'),
      summary: 'Stop the running timer and record its duration',
    },
    getActiveTimer: {
      method: 'GET',
      path: '/entries/timer/active',
      responses: {
        200: timeEntrySchema.nullable(),
        401: errorBodySchema,
      },
      // AI-visible (default): a self-scoped read, same visibility as listEntries above.
      metadata: routeMeta(null),
      summary: "The caller's own currently-running timer, or null",
    },

    // ---- Submit / decide / withdraw / lock -----------------------------------
    submitTimesheet: {
      method: 'POST',
      path: '/:timesheetId/submit',
      body: submitTimesheetBodySchema,
      responses: {
        200: timesheetSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta(['timesheet:submit', 'timesheet:read']),
      summary:
        'Submit a draft or rejected timesheet, starting a new approval workflow for this submission',
    },
    decideTimesheet: {
      method: 'POST',
      path: '/:timesheetId/decide',
      body: decideTimesheetBodySchema,
      responses: {
        200: timesheetSchema,
        401: errorBodySchema,
        // Includes the filer deciding their own timesheet — barred with no exceptions, not for HR
        // admin, not for org owner (see this file's header comment / plan §5).
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Approve or reject the step currently awaiting the caller',
    },
    withdrawTimesheet: {
      method: 'POST',
      path: '/:timesheetId/withdraw',
      body: c.noBody(),
      responses: {
        200: timesheetSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('timesheet:read'),
      summary: 'Recall a submitted timesheet back to draft before it is decided',
    },
    lockTimesheets: {
      method: 'POST',
      path: '/lock',
      body: lockTimesheetsBodySchema,
      responses: {
        200: lockTimesheetsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta('attendance.cycle:freeze'),
      summary: 'Lock approved timesheets past their policy auto-lock window',
    },

    // ---- Utilization --------------------------------------------------------
    getUtilization: {
      method: 'GET',
      path: '/utilization',
      query: getUtilizationQuerySchema,
      responses: {
        200: utilizationResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      metadata: routeMeta(null),
      summary: 'Utilization, billable utilization, and realization over a date range',
    },

    // ---- Register ---------------------------------------------------------------
    getRegister: {
      method: 'GET',
      path: '/register',
      query: getRegisterQuerySchema,
      responses: { 200: registerResponseSchema, 401: errorBodySchema, 403: errorBodySchema },
      metadata: routeMeta('timesheet:read'),
      summary: "Who has and has not submitted, for the people in the caller's timesheet:read scope",
    },

    // ---- Single timesheet (DECLARED LAST ON PURPOSE) ---------------------------
    // `GET /:timesheetId` matches any single path segment, so declared ahead of `GET /entries`
    // or `/utilization` it swallowed them (a uuid lookup for 'entries' -> 500).
    // Keep every literal-path GET above this line.
    // Enforced by apps/api/src/lib/contract-route-order.test.ts.
    getTimesheet: {
      method: 'GET',
      path: '/:timesheetId',
      responses: { 200: timesheetSchema, 401: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('timesheet:read'),
      summary: 'Get one timesheet',
    },
  },
  { pathPrefix: '/timesheets' },
);
